import { getNormalizedValue } from "../_Utils/getNormalizedValue";
import getMappingSchema from "../_Utils/getMappingSchema";
import { WAGE_MULTIPLIERS } from "../../../global/utils/constants";
import util from "../../../global/utils/Helpers";
import { normalizeCountyLabel } from "../../../global/utils/countyHelper";

const getMSACode = ({
  chosenState,
  chosenCounty,
  chosenMsaTitle,
  _blsCounties,
}) => {
  let bls_counties = _blsCounties && JSON.parse(_blsCounties);
  if (bls_counties && chosenMsaTitle) {
    const normalizedMsaTitle = chosenMsaTitle.toUpperCase();
    const normalizedState = chosenState.toUpperCase();
    const stateCounties = bls_counties?.[normalizedMsaTitle]?.[normalizedState];
    const normalizedCountyLabel = chosenCounty
      ? normalizeCountyLabel(chosenCounty.label || chosenCounty)
      : null;
    const countyData = stateCounties?.find((county) => {
      const countyBls = county.bls?.toUpperCase();
      if (countyBls === normalizedMsaTitle) {
        if (normalizedCountyLabel) {
          // If we have a county, find the exact match within the MSA
          return normalizeCountyLabel(county.label) === normalizedCountyLabel;
        }
        // If no county is provided, the first match for the MSA is sufficient
        return true;
      }
    });
    return countyData?.blsAreaCode;
  }
};

const normalizeAndGetMaxWage = (pw, pwUnit, altPw, altPwUnit) => {
  const normalizedPw =
    !!pw && !!pwUnit
      ? parseFloat(pw) * WAGE_MULTIPLIERS[pwUnit.toUpperCase()]
      : null;
  const normalizedAltPw =
    !!altPw && altPwUnit
      ? parseFloat(altPw) * WAGE_MULTIPLIERS[altPwUnit.toUpperCase()]
      : null;

  if (normalizedPw >= normalizedAltPw) {
    return { wage: pw, unit: pwUnit, normalizedWage: normalizedPw };
  }
  return { wage: altPw, unit: altPwUnit, normalizedWage: normalizedAltPw };
};

const getHighestWageAdditionalWorksites = (wageInfo, formId) => {
  if (formId === "9089") {
    return wageInfo.reduce(
      (max, curr) => {
        const currMax = normalizeAndGetMaxWage(
          curr.prevailingWage ?? -Infinity,
          curr.pwUnitOfPay ?? null,
          curr.altPrevailingWage ?? -Infinity,
          curr.altPwUnitOfPay ?? null
        );
        return currMax.normalizedWage > max.normalizedWage ? currMax : max;
      },
      { wage: -Infinity, unit: "Annual", normalizedWage: -Infinity }
    );
  }
  return null;
};

const pwPrefiller = ({
  formType,
  formApi,
  setPrefillLoading,
  data,
  selectedCaseData,
}) => {
  if (!data) {
    return null;
  }

  return new Promise((resolve, reject) => {
    setPrefillLoading(true);
    const { schemaPOC, schemaRest, schemaRestV2 } = getMappingSchema(formType);

    schemaPOC.forEach((mapping) => {
      let mapToField = mapping["to_d"];
      let valueToFill = data && data[mapping["from_internal"]];
      if (valueToFill && mapToField) {
        formApi.change(mapToField, valueToFill);
      }
    });

    // MAPPING FOR THE REST (SECTIONS B, E, D)
    schemaRest.forEach((mapping) => {
      let valueToFill = null;

      // if determined soc use that otherwise use suggested soc
      // do it differently depending on version
      const {
        formVersionId,
        pwdOnetCode,
        pwdOnetTitle,
        pwdLinkingSocCode,
        pwdSocTitle,
      } = data;

      let socFields;
      switch (`${formType}_v${formVersionId}`) {
        case "9142C_v1":
          socFields = {
            socCode: pwdLinkingSocCode,
            socTitle: pwdSocTitle,
            alternative_id: "_9141_alternative_id",
          };
          break;
        case "9142C_v2":
          socFields = {
            socCode: pwdOnetCode,
            socTitle: pwdOnetTitle,
            alternative_id: "_9141_alternative_id_v2",
          };
          break;
        default:
          socFields = {
            socCode: pwdOnetCode,
            socTitle: pwdOnetTitle,
            alternative_id: "_9141_alternative_id",
          };
      }

      let appianField = mapping["from_internal"];
      let socCodeExists =
        appianField === "suggestedSocCode" && !!socFields?.socCode;
      let socTitleExists =
        appianField === "suggestedSocTitle" && !!socFields?.socTitle;

      valueToFill =
        socCodeExists || socTitleExists
          ? getNormalizedValue(
              data[mapping[socFields?.alternative_id]],
              mapping?.type
            )
          : getNormalizedValue(data[appianField], mapping?.type);

      const worksiteFieldsList = [
        "employmentLocationWorksites", //9142B
        "appx_b_locations", //9142C
        "appx_b_worksites", //9089
      ];

      // A lot of this worksites logic is shared in pwPrefiller.js. Would be a good mini-refactor.
      if (worksiteFieldsList.includes(mapping["to"])) {
        // uppercase bls area name to match mapper's key format
        valueToFill = selectedCaseData[mapping["from_internal"]].map(
          (worksite) => {
            worksite = {
              determinationDate:
                selectedCaseData.caseMetadata.determinationDate || null,
              dateSubmitted: selectedCaseData.dateSubmitted || null,
              worksiteCity: worksite.worksiteCity || null,
              worksiteBlsArea: worksite.worksiteBlsArea
                ? worksite.worksiteBlsArea.toUpperCase()
                : null,
              worksiteCountyTownship: worksite.worksiteCountyTownship || null,
              worksiteState: worksite.worksiteState || null,
              worksitePostalCode: worksite.worksitePostalCode || null,
              pwdUnitOfPay: worksite.pwUnitOfPay || null,
              pwdWageRate: worksite?.prevailingWage?.toString() || null,
              rowNum: worksite?.rowNum?.toString() || null,
            };
            if (formType === "9089") {
              worksite.worksiteBlsAreaCode = getMSACode({
                chosenState: worksite?.worksiteState,
                chosenMsaTitle: worksite?.worksiteBlsArea,
                _blsCounties: data?._blsCounties,
              });
            }
            return worksite;
          }
        );
        // We make primary worksite available to linked in additional worksites
        let primaryWorksite = {
          worksiteCity: selectedCaseData.primaryWorksiteCity || null,
          worksiteState: selectedCaseData.primaryWorksiteState || null,
          worksitePostalCode:
            selectedCaseData.primaryWorksitePostalCode || null,
          worksiteCountyTownship:
            selectedCaseData.primaryWorksiteCounty?.label || null,
          worksiteBlsArea: selectedCaseData.primaryWorksiteBlsArea
            ? selectedCaseData.primaryWorksiteBlsArea.toUpperCase()
            : null,
          determinationDate:
            selectedCaseData.caseMetadata.determinationDate || null,
          dateSubmitted: selectedCaseData.dateSubmitted || null,
          pwdUnitOfPay: selectedCaseData.pwdUnitOfPay || null,
          pwdWageRate: selectedCaseData?.pwdWageRate?.toString() || null,
          rowNum: new Date().getTime().toString(),
        };
        if (formType === "9089") {
          primaryWorksite.worksiteBlsAreaCode = getMSACode({
            chosenState: selectedCaseData?.primaryWorksiteState,
            chosenMsaTitle: selectedCaseData?.primaryWorksiteBlsArea,
            _blsCounties: data?._blsCounties,
          });
          primaryWorksite.worksiteAddress1 =
            selectedCaseData?.primaryWorksiteAddr1;
          primaryWorksite.worksiteAddress2 =
            selectedCaseData?.primaryWorksiteAddr2;
        }
        valueToFill.unshift(primaryWorksite);
      }
      let mapToField = mapping["to"];

      valueToFill = util.setBadNaics(
        formType,
        data,
        mapping["from_internal"],
        valueToFill
      );

      formApi.change(mapToField, valueToFill);
    });

    // Determine highest wage for 9089
    if (formType === "9089") {
      // Just putting high level
      const baseLevelWageInfo = [
        {
          prevailingWage: data.pwdWageRate,
          pwUnitOfPay: data.pwdUnitOfPay,
          altPrevailingWage: data.altPwdWageRate,
          altPwUnitOfPay: data.altPwdUnitOfPay,
        },
      ];
      const allWageInfo = [...baseLevelWageInfo, ...data.otherWorksites];

      const highestWageInfo = getHighestWageAdditionalWorksites(
        allWageInfo,
        formType
      );

      formApi.change("pwdWageRate", util.toUSD(highestWageInfo.wage));
      formApi.change("pwdUnitOfPay", highestWageInfo.unit);
    }

    // This is a special field for 9142B for importing multiple fields into 1
    if (formType === "9142B") {
      let valueToFill = "";
      [
        "specReqLicenseCert",
        "specReqResFellow",
        "specReqForeignLang",
        "specReqOther",
      ].forEach((field) => {
        if (data[field]) {
          valueToFill += `${data[field]}\n`;
        }
      });
      formApi.change("jobMinspecialreq", valueToFill);
    }

    if (formType === "9142C") {
      if (formApi.getState().values["a5_is_emergency"] === "NO") {
        formApi.change("a7_9141_attached", "N/A");
      } else {
        formApi.change("a7_9141_attached", "YES");
      }
    }

    if (formType === "9089") {
      if (data.attyRepresentType === "None") {
        formApi.change("attyAgStateBarNumber", null);
        formApi.change("attyAgGoodStandingState", null);
        formApi.change("attyAgGoodStandingAttorney", null);
      }
      if (!!data.otherWorksites.length) {
        formApi.change("pwdHasMultipleWorksites", true);
        formApi.change("isMultipleLocations", "Yes");
        formApi.change("faWorksiteLinked", false);
        formApi.change("isAppdendixBAttached", 1);
        // Clear section F.a fields when multiple worksites exist
        // so filer can use the "Add Worksite" button to select from available worksites
        formApi.change("primaryWorksiteType", null);
        formApi.change("primaryWorksiteAddr1", null);
        formApi.change("primaryWorksiteAddr2", null);
        formApi.change("primaryWorksiteCity", null);
        formApi.change("primaryWorksiteCounty", null);
        formApi.change("primaryWorksiteState", null);
        formApi.change("primaryWorksitePostalCode", null);
        formApi.change("primaryWorksiteBlsArea", null);
        formApi.change("primaryWorksiteBlsAreaCode", null);
      } else {
        formApi.change("pwdHasMultipleWorksites", false);
        formApi.change("isMultipleLocations", "No");
        formApi.change("faWorksiteLinked", true);
        formApi.change("isAppdendixBAttached", 0);
        // First, determine the correct county object.
        // The `getCountyObject` helper will return the full {label, value} object.
        const msaCode = getMSACode({
          chosenState: data.primaryWorksiteState,
          chosenCounty: data?.primaryWorksiteCounty,
          chosenMsaTitle: data?.primaryWorksiteBlsArea,
          _blsCounties: data?._blsCounties,
        });
        formApi.change("primaryWorksiteAddr1", data.primaryWorksiteAddr1);
        formApi.change("primaryWorksiteAddr2", data.primaryWorksiteAddr2);
        formApi.change("primaryWorksiteCity", data.primaryWorksiteCity);
        formApi.change("primaryWorksiteCounty", data?.primaryWorksiteCounty);
        formApi.change("primaryWorksiteState", data.primaryWorksiteState);
        formApi.change(
          "primaryWorksitePostalCode",
          data.primaryWorksitePostalCode
        );
        formApi.change("primaryWorksiteBlsArea", data?.primaryWorksiteBlsArea);

        formApi.change("primaryWorksiteBlsAreaCode", msaCode);
        formApi.change(
          "_savedPrimaryWorksite",
          JSON.stringify({
            primaryWorksiteAddr1: data.primaryWorksiteAddr1 ?? null,
            primaryWorksiteAddr2: data.primaryWorksiteAddr2 ?? null,
            primaryWorksiteCity: data.primaryWorksiteCity ?? null,
            primaryWorksiteState: data.primaryWorksiteState ?? null,
            primaryWorksitePostalCode: data.primaryWorksitePostalCode ?? null,
            primaryWorksiteCounty: data?.primaryWorksiteCounty ?? null,
            primaryWorksiteBlsArea: data?.primaryWorksiteBlsArea ?? null,
            primaryWorksiteBlsAreaCode: msaCode,
          })
        );
      }
      if (data.primaryEducationLevel.toLowerCase() !== "none") {
        formApi.change("appendixABRequired", true);
      } else {
        formApi.change("appendixABRequired", false);
      }
      if (data.isSpecialRequirements === 1) {
        formApi.change("appendixADRequired", true);
      } else {
        formApi.change("appendixADRequired", false);
      }

      if (data.trainingRequired === 1 || data.isSpecReqLicenseCert === 1) {
        formApi.change("appendixACRequired", true);
      } else {
        formApi.change("appendixACRequired", false);
      }

      if (data.empExperienceRequired === 1 || data.altEmpExperienceReqd === 1) {
        formApi.change("appendixAERequired", true);
      } else {
        formApi.change("appendixAERequired", false);
      }
      if (data.cba === 1) {
        formApi.change("bargainingRep", 1);
        formApi.change("noBargainingRepPhysical", 0);
        formApi.change("noBargainingRepElectronic", 0);
        formApi.change("noBargainingRepInHouse", 0);
        formApi.change("noBargainingRepPrivate", 0);
        formApi.change("noBargainingRepNoNotice", 0);
      }
      //Additional field for E.2 attached 9141 confirmation
      if (
        formApi.getState().values.supervisedRecruitment?.toLowerCase() !== "no"
      ) {
        formApi.change("jobOppPWDAttached", "Yes");
      } else {
        formApi.change("jobOppPWDAttached", "N/A");
      }
    }

    schemaRestV2.forEach((mapping) => {
      let mapToField = mapping["to"];
      let valueToFillRest = getNormalizedValue(
        data[mapping["from_internal"]],
        mapping?.type
      );
      formApi.change(mapToField, valueToFillRest);
    });

    setPrefillLoading(false);
    resolve(data?.caseNumber);
  });
};

export default pwPrefiller;
